import React, {useEffect, useMemo, useState} from 'react'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {deDE} from '@mui/x-date-pickers/locales'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import FilterListIcon from '@mui/icons-material/FilterList'
import SearchIcon from '@mui/icons-material/Search'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {useTranslation} from 'react-i18next'
import dayjs, {Dayjs} from 'dayjs'
import 'dayjs/locale/de'
import {getSalesPersonMapData, SalesPersonMapData, SalesPersonMapEntry} from '../api/salesperson'
import {berlinToday} from '../utils/berlinDate'

export type MetricsCompareMode = 'patients' | 'implants' | 'extSensors'

function valueForMode(sp: SalesPersonMapEntry, mode: MetricsCompareMode): number {
  switch (mode) {
    case 'implants': return sp.implantCount
    case 'extSensors': return sp.extSensorsCount
    default: return sp.patientCount
  }
}

// The date actually queried for a given month: its last day, unless that hasn't happened yet
// (the current, still-running month) — then "today" stands in, same as the map's own default.
function lastRelevantDayOfMonth(month: Dayjs): Dayjs {
  const end = month.endOf('month')
  const today = berlinToday()
  return end.isAfter(today, 'day') ? today : end
}

function defaultRightDate(): Dayjs {
  return lastRelevantDayOfMonth(berlinToday())
}

function defaultLeftDate(): Dayjs {
  return lastRelevantDayOfMonth(berlinToday().subtract(1, 'month'))
}

interface Row {
  id: string
  lastName: string
  lastMonth: number
  thisMonth: number
  tmzLastMonth: number
  tmzThisMonth: number
  delta: number
}

function DeltaCell({delta, locale}: {delta: number; locale: string}) {
  const color = delta > 0 ? 'success.main' : delta < 0 ? 'error.main' : 'text.secondary'
  const Icon = delta > 0 ? TrendingUpIcon : delta < 0 ? TrendingDownIcon : TrendingFlatIcon
  return (
    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, color}}>
      <Icon fontSize="inherit"/>
      <Typography component="span" variant="body2" color="inherit" sx={{fontWeight: 600}}>
        {delta > 0 ? '+' : ''}{delta.toLocaleString(locale)}
      </Typography>
    </Box>
  )
}

interface SalesPersonMetricsTableProps {
  // The map's own already-fetched data above this table, plus the date and loading/error state
  // it was fetched under. This table's "last month" side defaults to the same date the map
  // itself defaults to (last completed month), so on a freshly-loaded page the two would
  // otherwise fire two identical requests; passing the map's own fetch down lets the table
  // mirror it instead of re-fetching, for as long as the two dates keep matching.
  mapData?: SalesPersonMapData | null
  mapDate?: Dayjs
  mapLoading?: boolean
  mapError?: boolean
}

// Standalone, adjustable month-over-month table: for each salesperson, shows the value of a
// user-selectable metric (patients by default, or implants / external sensors) on two
// independently pickable dates, the delta between them, and each date's TMZ count alongside it.
// Owns its own two dates and fetches its own data for whichever of them the map above isn't
// already covering — its dates and its metric mode still move entirely independently of the
// map's own view-mode / arbitrary-date comparison.
export default function SalesPersonMetricsTable({mapData, mapDate, mapLoading, mapError}: SalesPersonMetricsTableProps) {
  const {t, i18n} = useTranslation()
  const pickerLocaleText = i18n.language.startsWith('de')
    ? deDE.components.MuiLocalizationProvider.defaultProps.localeText
    : undefined

  const [mode, setMode] = useState<MetricsCompareMode>('patients')
  const [leftDate, setLeftDate] = useState<Dayjs>(defaultLeftDate)
  const [rightDate, setRightDate] = useState<Dayjs>(defaultRightDate)

  const [leftData, setLeftData] = useState<SalesPersonMapData | null>(null)
  const [leftLoading, setLeftLoading] = useState(false)
  const [leftError, setLeftError] = useState(false)
  const [rightData, setRightData] = useState<SalesPersonMapData | null>(null)
  const [rightLoading, setRightLoading] = useState(false)
  const [rightError, setRightError] = useState(false)

  // Ids the user has unchecked in the filter popover — absent here means "shown". Kept as an
  // exclusion set (rather than an inclusion list) so a salesperson who newly appears in the data
  // shows up by default instead of needing to be explicitly opted in.
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set())
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null)
  const [filterQuery, setFilterQuery] = useState('')

  const reusingMapData = mapDate != null && leftDate.isSame(mapDate, 'day')

  useEffect(() => {
    if (reusingMapData) {
      setLeftLoading(Boolean(mapLoading))
      setLeftError(Boolean(mapError))
      setLeftData(mapError ? null : mapData ?? null)
      return
    }
    let active = true
    setLeftLoading(true)
    setLeftError(false)
    getSalesPersonMapData(leftDate.format('YYYY-MM-DD'))
      .then(data => { if (active) { setLeftData(data); setLeftError(false) } })
      .catch(e => { if (active) { console.error(e); setLeftData(null); setLeftError(true) } })
      .finally(() => { if (active) setLeftLoading(false) })
    return () => { active = false }
  }, [leftDate, reusingMapData, mapData, mapLoading, mapError])

  useEffect(() => {
    let active = true
    setRightLoading(true)
    setRightError(false)
    getSalesPersonMapData(rightDate.format('YYYY-MM-DD'))
      .then(data => { if (active) { setRightData(data); setRightError(false) } })
      .catch(e => { if (active) { console.error(e); setRightData(null); setRightError(true) } })
      .finally(() => { if (active) setRightLoading(false) })
    return () => { active = false }
  }, [rightDate])

  const rows: Row[] = useMemo(() => {
    const byId = new Map<string, Row>()

    for (const sp of rightData?.salesPersons ?? []) {
      if (sp.id === '__unassigned__') continue
      byId.set(sp.id, {
        id: sp.id, lastName: sp.lastName,
        lastMonth: 0, thisMonth: valueForMode(sp, mode),
        tmzLastMonth: 0, tmzThisMonth: sp.tmzCount, delta: 0,
      })
    }

    for (const sp of leftData?.salesPersons ?? []) {
      if (sp.id === '__unassigned__') continue
      const existing = byId.get(sp.id)
      const lastMonth = valueForMode(sp, mode)
      if (existing) {
        existing.lastMonth = lastMonth
        existing.tmzLastMonth = sp.tmzCount
      } else {
        byId.set(sp.id, {
          id: sp.id, lastName: sp.lastName,
          lastMonth, thisMonth: 0,
          tmzLastMonth: sp.tmzCount, tmzThisMonth: 0, delta: 0,
        })
      }
    }

    const result = Array.from(byId.values())
    result.forEach(r => { r.delta = r.thisMonth - r.lastMonth })
    result.sort((a, b) => b.thisMonth - a.thisMonth || a.lastName.localeCompare(b.lastName))
    return result
  }, [rightData, leftData, mode])

  const visibleRows = useMemo(() => rows.filter(r => !hiddenIds.has(r.id)), [rows, hiddenIds])

  // Alphabetical, independent of the table's own this-month sort, so the filter list doesn't
  // reshuffle as the user checks/unchecks people.
  const filterOptions = useMemo(
    () => [...rows].sort((a, b) => a.lastName.localeCompare(b.lastName)),
    [rows],
  )

  // What the filter popover's checklist and its Select all / Clear all buttons act on — the
  // search narrows which salespeople you're toggling without touching anyone else's checked state.
  const searchedOptions = useMemo(() => {
    const q = filterQuery.trim().toLowerCase()
    if (!q) return filterOptions
    return filterOptions.filter(r => r.lastName.toLowerCase().includes(q))
  }, [filterOptions, filterQuery])

  const totals = useMemo(() => visibleRows.reduce((acc, r) => ({
    lastMonth: acc.lastMonth + r.lastMonth,
    thisMonth: acc.thisMonth + r.thisMonth,
    tmzLastMonth: acc.tmzLastMonth + r.tmzLastMonth,
    tmzThisMonth: acc.tmzThisMonth + r.tmzThisMonth,
    delta: acc.delta + r.delta,
  }), {lastMonth: 0, thisMonth: 0, tmzLastMonth: 0, tmzThisMonth: 0, delta: 0}), [visibleRows])

  function toggleHidden(id: string) {
    setHiddenIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const formatMonth = (d: Dayjs) =>
    i18n.language.startsWith('de') ? d.locale('de').format('MMMM YYYY') : d.format('MMMM YYYY')

  const metricLabel = mode === 'patients' ? t('map.patients')
    : mode === 'implants' ? t('map.viewModeImplants')
    : t('map.viewModeExtSensors')

  const isDefaultDates = leftDate.isSame(defaultLeftDate(), 'day') && rightDate.isSame(defaultRightDate(), 'day')

  // The picker only lets the user choose a month; the actual comparison date is always that
  // month's last (relevant) day, same as the fixed month-end behaviour this replaces.
  function pickMonth(value: Dayjs | null, fallback: Dayjs, onChange: (d: Dayjs) => void) {
    if (value == null) onChange(fallback)
    else if (value.isValid() && !value.isAfter(berlinToday(), 'month')) onChange(lastRelevantDayOfMonth(value))
  }

  return (
    <Box sx={{mt: 3}}>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1}}>
        <Typography variant="subtitle1" sx={{fontWeight: 600}}>
          {t('map.metricsTable.title')}
        </Typography>
        {!isDefaultDates && (
          <Button size="small" variant="text" onClick={() => { setLeftDate(defaultLeftDate()); setRightDate(defaultRightDate()) }}>
            {t('map.metricsTable.resetDates')}
          </Button>
        )}
      </Box>

      <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language.startsWith('de') ? 'de' : 'en'} localeText={pickerLocaleText}>
            <DatePicker
              views={['year', 'month']}
              openTo="month"
              maxDate={berlinToday()}
              format="MMMM YYYY"
              value={leftDate}
              onChange={v => pickMonth(v, defaultLeftDate(), setLeftDate)}
              slotProps={{
                textField: {size: 'small', label: t('map.metricsTable.leftDatePicker')},
              }}
            />
          </LocalizationProvider>
          <ArrowForwardIcon fontSize="small" sx={{color: 'text.secondary'}}/>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language.startsWith('de') ? 'de' : 'en'} localeText={pickerLocaleText}>
            <DatePicker
              views={['year', 'month']}
              openTo="month"
              maxDate={berlinToday()}
              format="MMMM YYYY"
              value={rightDate}
              onChange={v => pickMonth(v, defaultRightDate(), setRightDate)}
              slotProps={{
                textField: {size: 'small', label: t('map.metricsTable.rightDatePicker')},
              }}
            />
          </LocalizationProvider>
        </Box>

        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_e, val) => { if (val) setMode(val) }}
        >
          <ToggleButton value="patients">{t('map.patients')}</ToggleButton>
          <ToggleButton value="implants">{t('map.viewModeImplants')}</ToggleButton>
          <ToggleButton value="extSensors">{t('map.viewModeExtSensors')}</ToggleButton>
        </ToggleButtonGroup>

        <Tooltip title={t('map.metricsTable.filterSalespeople')}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FilterListIcon/>}
            onClick={e => setFilterAnchor(e.currentTarget)}
          >
            {t('map.metricsTable.filterCount', {shown: visibleRows.length, total: rows.length})}
          </Button>
        </Tooltip>

        <Popover
          open={Boolean(filterAnchor)}
          anchorEl={filterAnchor}
          onClose={() => { setFilterAnchor(null); setFilterQuery('') }}
          anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          transformOrigin={{vertical: 'top', horizontal: 'right'}}
        >
          <Box sx={{p: 1.5, minWidth: 240, maxWidth: 280}}>
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder={t('common.search')}
              value={filterQuery}
              onChange={e => setFilterQuery(e.target.value)}
              sx={{mb: 1}}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>,
                },
              }}
            />
            <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 0.5}}>
              <Button
                size="small"
                onClick={() => setHiddenIds(prev => {
                  const next = new Set(prev)
                  searchedOptions.forEach(r => next.delete(r.id))
                  return next
                })}
                disabled={searchedOptions.length === 0 || searchedOptions.every(r => !hiddenIds.has(r.id))}
              >
                {t('map.metricsTable.selectAll')}
              </Button>
              <Button
                size="small"
                onClick={() => setHiddenIds(prev => {
                  const next = new Set(prev)
                  searchedOptions.forEach(r => next.add(r.id))
                  return next
                })}
                disabled={searchedOptions.length === 0 || searchedOptions.every(r => hiddenIds.has(r.id))}
              >
                {t('map.metricsTable.clearAll')}
              </Button>
            </Box>
            <Box sx={{maxHeight: 320, overflowY: 'auto'}}>
              {searchedOptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{px: 1, py: 0.5}}>
                  {t('map.metricsTable.noMatches')}
                </Typography>
              ) : searchedOptions.map(r => (
                <FormControlLabel
                  key={r.id}
                  sx={{display: 'flex', width: '100%', ml: 0}}
                  control={
                    <Checkbox
                      size="small"
                      checked={!hiddenIds.has(r.id)}
                      onChange={() => toggleHidden(r.id)}
                    />
                  }
                  label={<Typography variant="body2">{r.lastName}</Typography>}
                />
              ))}
            </Box>
          </Box>
        </Popover>
      </Box>

      {rightError || leftError ? (
        <Typography variant="body2" color="error">{t('activities.dataFetchFail')}</Typography>
      ) : rightLoading || leftLoading ? (
        // Hide on every reload, not just the first — otherwise the header (driven by the picker,
        // which updates the instant a month is picked) can briefly show a different month than the
        // still-stale rows underneath it, with the delta computed across two mismatched months.
        <Box sx={{display: 'flex', alignItems: 'center', gap: 2, py: 3}}>
          <CircularProgress size={22}/>
          <Typography variant="body2" color="text.secondary">{t('map.loading')}</Typography>
        </Box>
      ) : (
        <>
          {leftData?.assignmentBasis === 'current' && (
            <Typography variant="caption" color="warning.main" sx={{mb: 0.5, display: 'block'}}>
              {t('map.metricsTable.assignmentCurrentBasis', {month: formatMonth(leftDate)})}
            </Typography>
          )}
          {rightData?.assignmentBasis === 'current' && (
            <Typography variant="caption" color="warning.main" sx={{mb: 1, display: 'block'}}>
              {t('map.metricsTable.assignmentCurrentBasis', {month: formatMonth(rightDate)})}
            </Typography>
          )}

          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{t('map.noData')}</Typography>
          ) : visibleRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{t('map.metricsTable.allFiltered')}</Typography>
          ) : (
            <Paper variant="outlined" sx={{overflowX: 'auto'}}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{fontWeight: 600}}>{t('map.salesperson')}</TableCell>
                    <TableCell align="right" sx={{fontWeight: 600}}>
                      {t('map.metricsTable.lastMonth', {metric: metricLabel})}
                      <Typography component="div" variant="caption" color="text.secondary">{formatMonth(leftDate)}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{fontWeight: 600}}>
                      {t('map.tmz')}
                      <Typography component="div" variant="caption" color="text.secondary">{formatMonth(leftDate)}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{fontWeight: 600}}>
                      {t('map.metricsTable.thisMonth', {metric: metricLabel})}
                      <Typography component="div" variant="caption" color="text.secondary">{formatMonth(rightDate)}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{fontWeight: 600}}>
                      {t('map.tmz')}
                      <Typography component="div" variant="caption" color="text.secondary">{formatMonth(rightDate)}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{fontWeight: 600}}>{t('map.metricsTable.delta')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map(r => (
                    <TableRow key={r.id}>
                      <TableCell sx={{fontSize: 13}}>{r.lastName}</TableCell>
                      <TableCell align="right" sx={{fontSize: 13}}>{r.lastMonth.toLocaleString(i18n.language)}</TableCell>
                      <TableCell align="right" sx={{fontSize: 13}}>{r.tmzLastMonth.toLocaleString(i18n.language)}</TableCell>
                      <TableCell align="right" sx={{fontWeight: 600, fontSize: 13}}>{r.thisMonth.toLocaleString(i18n.language)}</TableCell>
                      <TableCell align="right" sx={{fontSize: 13}}>{r.tmzThisMonth.toLocaleString(i18n.language)}</TableCell>
                      <TableCell align="right"><DeltaCell delta={r.delta} locale={i18n.language}/></TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{fontWeight: 600}}>{t('map.total')}</TableCell>
                    <TableCell align="right" sx={{fontWeight: 700}}>{totals.lastMonth.toLocaleString(i18n.language)}</TableCell>
                    <TableCell align="right" sx={{fontWeight: 700}}>{totals.tmzLastMonth.toLocaleString(i18n.language)}</TableCell>
                    <TableCell align="right" sx={{fontWeight: 700}}>{totals.thisMonth.toLocaleString(i18n.language)}</TableCell>
                    <TableCell align="right" sx={{fontWeight: 700}}>{totals.tmzThisMonth.toLocaleString(i18n.language)}</TableCell>
                    <TableCell align="right"><DeltaCell delta={totals.delta} locale={i18n.language}/></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </Box>
  )
}
